import { useState } from 'react';
import { TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import type {
  EmployeeEntry,
  EmployeeEntryErrors,
} from '../../types/addEmployeeType';

const GENDER_ITEMS = [
  {
    id: 'male',
    label: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_MALE,
    toolTip: false,
  },
  {
    id: 'female',
    label: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_FEMALE,
    toolTip: false,
  },
];

interface AddEmployeeFormProps {
  entry: EmployeeEntry;
  errors: EmployeeEntryErrors;
  isSubmitting: boolean;
  cancelAlwaysEnabled?: boolean;
  onFieldChange: (
    field: keyof Pick<
      EmployeeEntry,
      'name' | 'email' | 'phone' | 'academicTitle'
    >,
    value: string
  ) => void;
  onGenderChange?: (value: 'male' | 'female' | undefined) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const AddEmployeeForm = ({
  entry,
  errors,
  isSubmitting,
  cancelAlwaysEnabled = false,
  onFieldChange,
  onGenderChange,
  onSubmit,
  onCancel,
}: AddEmployeeFormProps) => {
  const isDirty =
    entry.name !== '' ||
    entry.email !== '' ||
    entry.phone !== '' ||
    !!entry.gender ||
    (entry.academicTitle ?? '') !== '';
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const handleCancel = () => {
    if (isDirty) {
      setConfirmClearOpen(true);
    } else {
      onCancel();
    }
  };

  const selectedGenderLabel = entry.gender
    ? GENDER_ITEMS.find((g) => g.id === entry.gender)?.label
    : undefined;

  return (
    <div className="border border-primary-color-30 p-4 rounded-2xl flex flex-col gap-6">
      <CreateModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirmCancel={() => {
          setConfirmClearOpen(false);
          onCancel();
        }}
      />
      <p className="text-[18px] font-normal text-neutral-10">
        {FACILITY_LABELS.SECTION_EMPLOYEE_FORM}
      </p>

      {/* Row 1: name | email | phone */}
      <div className="flex gap-4">
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_NAME}
            dataTestId="employee-name"
            autoTrimMaxLength={200}
            value={entry.name}
            onChange={(e) => onFieldChange('name', e.target.value.trimStart())}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            disabled={isSubmitting}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_EMAIL}
            dataTestId="employee-email"
            autoTrimMaxLength={100}
            value={entry.email}
            onChange={(e) =>
              onFieldChange('email', e.target.value.replace(/\s/g, ''))
            }
            error={Boolean(errors.email)}
            errorMessage={errors.email}
            disabled={isSubmitting}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={FACILITY_LABELS.FIELD_EMPLOYEE_PHONE}
            dataTestId="employee-phone"
            type="tel"
            value={entry.phone}
            onChange={(e) =>
              onFieldChange(
                'phone',
                e.target.value.replace(/\D/g, '').slice(0, 9)
              )
            }
            error={Boolean(errors.phone)}
            errorMessage={errors.phone}
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* Row 2: gender dropdown | academic title | empty (matches 3-col layout of row 1) */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Dropdown
            items={GENDER_ITEMS}
            {...(entry.gender !== undefined && { selectedId: entry.gender })}
            onItemClick={(id) => onGenderChange?.(id as 'male' | 'female')}
            menuFullWidth
            disabled={isSubmitting}
            error={Boolean(errors.gender)}
            {...(errors.gender ? { errorMessage: errors.gender } : {})}
            trigger={({ open, toggle, disabled, error }) => (
              <DropDownButton
                label={
                  <>
                    {FACILITY_LABELS.FIELD_EMPLOYEE_GENDER}
                    <span className="text-bodySmall font-normal">
                      {' '}
                      {FACILITY_LABELS.FIELD_OPTIONAL}
                    </span>
                  </>
                }
                open={open}
                toggle={toggle}
                disabled={disabled}
                error={error}
                data-testid="employee-gender"
                {...(selectedGenderLabel !== undefined && {
                  value: selectedGenderLabel,
                })}
              />
            )}
          />
        </div>
        <div className="flex-1">
          <TextField
            fullWidth
            label={
              <>
                {FACILITY_LABELS.FIELD_EMPLOYEE_ACADEMIC_TITLE}
                <span className="text-bodySmall font-normal">
                  {' '}
                  {FACILITY_LABELS.FIELD_OPTIONAL}
                </span>
              </>
            }
            dataTestId="employee-academic-title"
            autoTrimMaxLength={200}
            value={entry.academicTitle ?? ''}
            onChange={(e) =>
              onFieldChange('academicTitle', e.target.value.trimStart())
            }
            error={Boolean(errors.academicTitle)}
            errorMessage={errors.academicTitle}
            disabled={isSubmitting}
          />
        </div>
        <div className="flex-1" />
      </div>

      <div className="mt-[16px] -mx-4 border-b-2 border-[#DCDDE0]" />

      {/* Action buttons */}
      <div className="flex justify-end gap-4 mt-[-8px]">
        <Button
          label={FACILITY_LABELS.CANCEL_BUTTON}
          buttonType="Square"
          type="button"
          onClick={handleCancel}
          disabled={isSubmitting || (!cancelAlwaysEnabled && !isDirty)}
          className="!bg-transparent disabled:!bg-neutral-10/15 !px-3 !py-1.5 cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant disabled:!text-neutral-10/[0.38] hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
        />
        <Button
          label={
            isSubmitting
              ? FACILITY_LABELS.ADD_EMPLOYEE_BUTTON_LOADING
              : FACILITY_LABELS.ADD_EMPLOYEE_BUTTON
          }
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || !isDirty}
          className="!px-3 !py-1.5 text-labelLarge cursor-pointer !rounded-[8px]"
        />
      </div>
    </div>
  );
};
